import RefreshIcon from "@assets/images/refresh-primary.svg";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { FIELD_TYPE } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import OrderStatus from "@saleor/changeOrderExport/components/OrderStatus";
import DatePicker from "@saleor/components/DatePicker";
import { FormSpacer } from "@saleor/components/FormSpacer";
import MultipleAutocompleteTextInput from "@saleor/components/MultipleAutocompleteTextInput";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import { LmsReportCsCustomerInput } from "@saleor/graphql";
import useDeliveryReportAdmin from "@saleor/hooks/useDeliveryReportAdmin";
import { mapEdgesToItems } from "@saleor/utils/maps";
import cls from "classnames";
import moment from "moment";
import React, { useEffect, useMemo } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import useStyles from "./styles";

export interface OrderListFilterProps {
  handleChangeFilter?: (filterObj: LmsReportCsCustomerInput) => void;
  loading?: boolean;
  message: JSX.Element | string;
  listOrder: any[];
}

const OrderFilterSectionAdmin: React.FC<OrderListFilterProps> = ({
  handleChangeFilter,
  message,
  loading,
  listOrder
}) => {
  const MAX_PERIOD_DATE = 2;
  const classes = useStyles();
  const {
    formik,
    // setDataFormik,
    isOpen,
    setIsOpen,
    isClean,
    handleCleanForm,
    handleCleanDates,
    formData,
    refetchSalesOrg,
    ArrowIconComponent,
    getMaterialAdminOrder,
    fetchMaterialAdminOrder,
    loadingMaterialAdminOrder,
    fetchSoldToDataSuggestions,
    getSoldToDataSuggestions,
    loadingSoldToDataSuggestion
  } = useDeliveryReportAdmin({ handleChangeFilter });

  useEffect(() => {
    if (listOrder?.length && listOrder?.length > 0 && !loading) {
      setIsOpen(false);
    }
  }, [listOrder, loading]);

  const getTextError = field => (
    <div className={classes.textError}>{formik.errors[field]}</div>
  );

  const dateCheck =
    (formik.values.orderCreateDate.gte && formik.values.orderCreateDate.lte) ||
    (!formik.values.orderCreateDate.gte && !formik.values.orderCreateDate.lte);

  const isDisabled = useMemo(
    () =>
      !(
        (dateCheck && formik.values.soNo) ||
        (dateCheck && formik.values.poNo && formik.values.soldTo.length) ||
        (formik.values.soldTo.length &&
          formik.values.orderCreateDate.gte &&
          formik.values.orderCreateDate.lte)
      ) ||
      !formik.values.bu ||
      !formik.values.saleOrganization.length,
    [formik.values]
  );

  const mappingDataSuggest = name => {
    if (name === "material") {
      const filterSoldToDomesticOrder =
        fetchMaterialAdminOrder?.filterRequireAttentionMaterial;
      return {
        searchField: "material",
        getDataSuggestions: getMaterialAdminOrder,
        loadingDataSuggestion: loadingMaterialAdminOrder,
        isCheckingRole: true,
        dataOptions:
          mapEdgesToItems(filterSoldToDomesticOrder)?.map(data => ({
            title: `${data.code} - ${data.descriptionEn}`,
            value: data.code
          })) || []
      };
    }
    if (name === "soldTo") {
      const filterSoldToDomesticOrder =
        fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos;
      return {
        // totalCount: filterSoldToDomesticOrder?.totalCount,
        pageInfo: filterSoldToDomesticOrder?.pageInfo,
        showLoadMore: true,
        fetchDataSuggestions: fetchSoldToDataSuggestions,
        loadingDataSuggestion: loadingSoldToDataSuggestion,
        getDataSuggestions: getSoldToDataSuggestions,
        searchField: "soldTo",
        dataOptions:
          mapEdgesToItems(filterSoldToDomesticOrder)?.map(data => ({
            title: `${data.code} - ${data.name}`,
            value: `${data.code} - ${data.name}`
          })) || []
      };
    }
  };

  const layoutGrid = child => {
    if (child.name === "orderCreateDate") {
      return 6;
    }

    if (
      child.type === FIELD_TYPE.SUGGESTION_INPUT ||
      child.type === FIELD_TYPE.DATEPICKER
    ) {
      return 6;
    }

    return 3;
  };
  const layoutGridSm = child => {
    if (
      child.type === FIELD_TYPE.DATEPICKER ||
      child.type === FIELD_TYPE.SUGGESTION_INPUT
    ) {
      return 12;
    }
    return 6;
  };

  useEffect(() => {
    if (formik.values.orderCreateDate.lte) {
      const diff = moment(formik.values.orderCreateDate.lte).diff(
        moment(formik.values.orderCreateDate.gte),
        "days"
      );
      if (Math.abs(diff) > MAX_PERIOD_DATE) {
        const newLte = moment(formik.values.orderCreateDate.gte)
          .add(MAX_PERIOD_DATE, "days")
          .format("YYYY-MM-DD");
        formik.setFieldValue("orderCreateDate.lte", newLte);
      }
    }
  }, [formik.values.orderCreateDate.gte]);

  useEffect(() => {
    if (formik.values.orderCreateDate.gte) {
      const diff = moment(formik.values.orderCreateDate.gte).diff(
        moment(formik.values.orderCreateDate.lte),
        "days"
      );
      if (Math.abs(diff) > MAX_PERIOD_DATE) {
        const newGte = moment(formik.values.orderCreateDate.lte)
          .subtract(MAX_PERIOD_DATE, "days")
          .format("YYYY-MM-DD");
        formik.setFieldValue("orderCreateDate.gte", newGte);
      }
    }
  }, [formik.values.orderCreateDate.lte]);

  const fetchSaleOrgChoices = value => {
    refetchSalesOrg({
      soldToCode: value?.[0]?.split("-")[0]?.trim() || ""
    });
  };

  const renderItem = (
    <Grid className={classes.searchBox} container spacing={3}>
      {formData.map((child, key) => {
        const renderChild = (children: JSX.Element, childReplace?: any) => (
          <Grid
            item
            md={!childReplace ? layoutGrid(child) : 12}
            sm={!childReplace ? layoutGridSm(child) : 12}
            xs={12}
            key={key}
            className={
              !childReplace &&
              cls({
                [classes.gridDatePicker]:
                  (childReplace || child).type === FIELD_TYPE.DATEPICKER
              })
            }
          >
            <div
              className={cls({
                [classes.boxDatePicker]:
                  (childReplace || child).type === FIELD_TYPE.DATEPICKER
              })}
            >
              <MyTypography variant={"subtitle3"}>
                {(childReplace || child).title}
                {(childReplace || child).required ? (
                  <span className={classes.required}>*</span>
                ) : (
                  ""
                )}
                {(childReplace || child).subTitle ? (
                  <span className={classes.subTitle}>{` ${
                    (childReplace || child).subTitle
                  }`}</span>
                ) : (
                  ""
                )}
              </MyTypography>
              {children}
              {(childReplace || child).type !== FIELD_TYPE.DATEPICKER &&
              (childReplace || child).name !== "soldTo"
                ? getTextError((childReplace || child).name)
                : ""}
            </div>
          </Grid>
        );
        if (child.type === FIELD_TYPE.MULTIPLE_SELECT) {
          return renderChild(
            <OrderStatus
              disableIndeterminate
              showChipDeleteButton={false}
              choices={child.options}
              name={child.name}
              id={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              showCircularIcon={false}
              isClean={isClean}
              placeholder={child.placeholder}
              listItemClass={classes.listItemClass}
              numberItemDisplay={2}
              error={!!formik.errors[child.name]}
              ignoreRerender={child?.ignoreRerender}
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_DATE) {
          return renderChild(
            <DatePicker
              name={`${child.name}`}
              id={`${child.name}`}
              data-testid={child.name}
              disableManualInput
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              format="YYYY-MM-DD"
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_INPUT) {
          return renderChild(
            <>
              <SingleTextField
                name={child.name}
                value={formik.values[child.name]}
                id={child.name}
                testId={child.name}
                onChange={formik.handleChange}
                placeholder={child.placeholder}
                disabled={child.disabled}
                error={!!formik.errors[child.name]}
                maxLengthProps={child?.maxLength ? child.maxLength : undefined}
              />
            </>
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_SELECT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              testId={child.name}
              onChange={formik.handleChange}
              value={formik.values[child.name]}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
            />
          );
        }

        if (child.type === FIELD_TYPE.SUGGESTION_INPUT) {
          const dataSuggest = mappingDataSuggest(child.name);
          if (child.name === "soldTo") {
            return renderChild(
              <MultipleAutocompleteTextInput
                maxCount={1}
                reRender={isClean}
                fullWidthItem
                name={child.name}
                id={child.name}
                testId={child.name}
                value={formik.values.soldTo}
                getByValue
                keyFilterApi={"search"}
                headerText={"Sold-to ที่เลือกค้นหา"}
                getDataSuggestions={dataSuggest.getDataSuggestions}
                fetchDataSuggestions={dataSuggest.fetchDataSuggestions}
                loadingDataSuggestion={dataSuggest.loadingDataSuggestion}
                dataOptions={dataSuggest.dataOptions}
                message={"ยังไม่มีข้อมูลที่เลือกค้นหา"}
                placeholder={"ค้นหา Sold-to"}
                handleSelectedSearch={value => {
                  formik.handleChange({
                    target: {
                      name: "soldTo",
                      value
                    }
                  });
                  fetchSaleOrgChoices(value);
                }}
                error={!!formik.errors[child.name]}
                textError={getTextError(child.name)}
              />
            );
          } else if (child.name === "material") {
            const dataSuggestMaterial = mappingDataSuggest(child.name);
            return renderChild(
              <MultipleAutocompleteTextInput
                reRender={isClean}
                maxCount={1}
                fullWidthItem
                name={child.name}
                id={child.name}
                testId={child.name}
                getByValue
                isCheckingRole
                keyFilterApi={"material"}
                headerText={"Material Code / Description ที่เลือกค้นหา"}
                getDataSuggestions={dataSuggestMaterial.getDataSuggestions}
                fetchDataSuggestions={dataSuggestMaterial.fetchDataSuggestions}
                loadingDataSuggestion={
                  dataSuggestMaterial.loadingDataSuggestion
                }
                dataOptions={dataSuggestMaterial.dataOptions}
                message={"ยังไม่มีข้อมูลที่เลือกค้นหา"}
                placeholder={"ค้นหา Material Code / Description"}
                handleSelectedSearch={value => {
                  formik.handleChange({
                    target: {
                      name: "material",
                      value
                    }
                  });
                }}
              />
            );
          }
        }

        if (child.type === FIELD_TYPE.RADIO_GROUP) {
          return renderChild(
            <RadioGroup
              className={classes.radioGroup}
              aria-labelledby="demo-controlled-radio-buttons-group"
              name={child.name}
              id={child.name}
              data-testid={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
            >
              {child.options?.map((o, oKey) => (
                <FormControlLabel
                  key={`radio${oKey}`}
                  id={`radio${oKey}`}
                  data-testid={`radio${oKey}`}
                  value={o.value}
                  control={<Radio />}
                  label={
                    <MyTypography variant={"body3"}>{o.label}</MyTypography>
                  }
                />
              ))}
            </RadioGroup>
          );
        }
        if (child.type === FIELD_TYPE.DATEPICKER) {
          return renderChild(
            <Box display={"flex"} justifyItems="center" alignItems={"start"}>
              <div className={classes.itemDatePicker}>
                <DatePicker
                  isUpdateDate
                  type="gte"
                  dateRange={3}
                  error={!!formik.errors[child.name]?.gte}
                  name={`${child.name}_gte`}
                  id={`${child.name}_gte`}
                  data-testid={`${child.name}_gte`}
                  disableManualInput
                  value={formik.values[child.name]?.gte || ""}
                  onChange={e => {
                    // changeDate(child.name, e.target.value, true);
                    formik.handleChange({
                      target: {
                        name: `${child.name}.gte`,
                        value: e.target.value
                      }
                    });
                  }}
                  format="YYYY-MM-DD"
                  maxDate={
                    formik.values[child.name]?.lte
                      ? moment(formik.values[child.name]?.lte)
                      : undefined
                  }
                  minDate={undefined}
                />
                <div className={classes.textError}>
                  {formik.errors.orderCreateDate?.gte}
                </div>
              </div>
              <Box
                style={{
                  width: 20,
                  height: 2,
                  background: "#999999"
                }}
                mt={2}
                mx={1}
              ></Box>
              <div className={classes.itemDatePicker}>
                <DatePicker
                  isUpdateDate
                  type="lte"
                  dateRange={3}
                  error={!!formik.errors[child.name]?.lte}
                  name={`${child.name}_lte`}
                  id={`${child.name}_lte`}
                  data-testid={`${child.name}_lte`}
                  disableManualInput
                  value={formik.values[child.name]?.lte || ""}
                  onChange={e => {
                    formik.handleChange({
                      target: {
                        name: `${child.name}.lte`,
                        value: e.target.value
                      }
                    });
                  }}
                  format="YYYY-MM-DD"
                  minDate={
                    formik.values[child.name].gte
                      ? moment(formik.values[child.name].gte)
                      : undefined
                  }
                  maxDate={undefined}
                />
                <div className={classes.textError}>
                  {formik.errors.orderCreateDate?.lte}
                </div>
              </div>
              <Box
                style={{
                  height: 2,
                  background: "#999999"
                }}
                mt={2}
                mx={1}
              />
              <div className={""}>
                <Button
                  className={classes.resetButton}
                  variant={"outlined"}
                  color={"secondary"}
                  disabled={
                    !formik.values.orderCreateDate.lte &&
                    !formik.values.orderCreateDate.gte
                  }
                  onClick={handleCleanDates}
                  name="resetDateButton"
                  id="resetDateButton"
                >
                  <SVG
                    src={RefreshIcon}
                    className={classes.icon}
                    width={16}
                    height={16}
                  />
                  <FormattedMessage defaultMessage="ล้างวันที่" />
                </Button>
              </div>
            </Box>
          );
        }
      })}
    </Grid>
  );

  return (
    <>
      <div className={classes.wrapper}>
        <Box className={classes.root}>
          <Grid
            className={classes.header}
            container
            onClick={() => setIsOpen(!isOpen)}
          >
            <MyTypography variant="h6">
              <FormattedMessage defaultMessage="ค้นหา Delivery Report (LMS)" />
            </MyTypography>
            <ArrowIconComponent className={classes.arrowIcon} />
          </Grid>
          <Collapse in={isOpen}>
            {renderItem}
            <Box className={classes.divider}>
              <FormSpacer />
              <Divider />
            </Box>
            <Box className={classes.bottomBar} ml={3}>
              <Box>{message}</Box>
              <Box className={classes.flex}>
                <Box mr={2}>
                  <Button
                    className={classes.resetButton}
                    variant={"outlined"}
                    color={"secondary"}
                    disabled={!formik.dirty}
                    onClick={handleCleanForm}
                    name="resetButton"
                    id="resetButton"
                  >
                    <SVG
                      src={RefreshIcon}
                      className={classes.icon}
                      width={16}
                      height={16}
                    />
                    <FormattedMessage defaultMessage="ล้างข้อมูล" />
                  </Button>
                </Box>
                <Box>
                  <Button
                    className={classes.actionButton}
                    variant={"contained"}
                    disabled={isDisabled}
                    color={"secondary"}
                    onClick={formik.submitForm}
                    name="submitButton"
                    id="submitButton"
                  >
                    <SearchIcon style={{ fontSize: "18px" }} />
                    <FormattedMessage defaultMessage="ค้นหา" />
                  </Button>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </div>
    </>
  );
};

export default OrderFilterSectionAdmin;
